import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { getApiErrorMessage, useFormWithValidation, useProjectSiteParams } from '@/shared/lib';
import {
  type Contact,
  useCreateContactMutation,
  useGetContactQuery,
  useUpdateContactMutation
} from '@/entities/contacts';

import { createContactSchema, DEFAULT_CONTACT_VALUES, type ContactFormValues } from './validation';
import type { ModalMode } from './types';
import { transformContactToForm, transformFormToContact } from '../lib';

interface UseContactFormProps {
  isOpen: boolean;
  mode: ModalMode;
  /** The contact to view or edit. */
  contactId?: string;
  /** The organization a created contact belongs to. */
  orgId?: number;
  onSuccess: (contact: Contact) => void;
  onCancel: () => void;
}

/**
 * The contact form of the modal. View and edit read the contact afresh on every opening and fill
 * the form from `currentData` only — it stays empty until this opening's response arrives, so the
 * form never starts from a copy read before someone else saved the contact (`PUT` replaces it
 * whole). The form is filled once per opening: the refetch a save triggers does not reset it.
 */
export const useContactForm = ({ isOpen, mode, contactId, orgId, onSuccess, onCancel }: UseContactFormProps) => {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const isCreateMode = mode === 'create';

  const [createContact, { isLoading: isCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();

  const { currentData: contact, isError: loadFailed } = useGetContactQuery(
    { id: contactId ?? '', projectId: projectId ?? 0, siteId: siteId ?? 0 },
    { skip: !isOpen || isCreateMode || !contactId || !projectId || !siteId }
  );

  const contactSchema = createContactSchema(t);
  const form = useFormWithValidation(contactSchema, DEFAULT_CONTACT_VALUES);

  const filledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      filledFor.current = null;
      return;
    }
    const target = isCreateMode ? mode : contact?.id;
    if (!target || filledFor.current === target) return;
    filledFor.current = target;
    form.reset(contact && !isCreateMode ? transformContactToForm(contact) : DEFAULT_CONTACT_VALUES);
  }, [isOpen, isCreateMode, mode, contact, form]);

  const onSubmit = useCallback(
    async (values: ContactFormValues) => {
      const contactOrgId = isCreateMode ? orgId : contact?.orgId;
      if (!projectId || !siteId || !contactOrgId) {
        toast.error(t('common.errors.missingParams', 'Missing required parameters'));
        return;
      }

      const payload = { ...transformFormToContact(values, contactOrgId), projectId, siteId };
      try {
        const result = isCreateMode
          ? await createContact(payload).unwrap()
          : await updateContact({ ...payload, id: contact!.id }).unwrap();

        toast.success(
          isCreateMode
            ? t('contacts.createSuccess', 'Contact created successfully')
            : t('contacts.updateSuccess', 'Contact updated successfully')
        );
        onSuccess(result);
      } catch (error) {
        toast.error(getApiErrorMessage(error, t('contacts.saveError', 'Failed to save contact')));
      }
    },
    [isCreateMode, orgId, contact, projectId, siteId, createContact, updateContact, onSuccess, t]
  );

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: !isCreateMode && !contact && !loadFailed,
    loadFailed: !isCreateMode && loadFailed,
    isSaving: isCreating || isUpdating,
    onCancel
  };
};
